import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronRight, MessageSquare, Loader2, Lock } from "lucide-react";
import { AgentSdkThread, AGENT_SDK_THREAD_STATUS } from "@forgepoint/shared";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useUserDisplayNames } from "@/store/teamsStore";

interface OpportunityThreadRowProps {
  thread: AgentSdkThread;
  onClick: (threadId: string) => void;
}

/**
 * A single thread row in the opportunity threads section.
 * Conversations show last activity and only animate while the agent is running.
 * Private threads the user cannot access are shown read-only (title + owner) and are
 * not clickable, so the user knows a thread exists without seeing its content.
 *
 * @param thread - The thread to render
 * @param onClick - Callback to navigate to the thread
 */
export const OpportunityThreadRow: React.FC<OpportunityThreadRowProps> = ({
  thread,
  onClick,
}) => {
  const locked = thread.accessible === false;
  const userDisplayNames = useUserDisplayNames();
  const ownerName = thread.userId
    ? userDisplayNames.get(thread.userId)
    : undefined;

  const rowContent = (
    <>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {locked ? (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Lock className="h-3 w-3" />
          </span>
        ) : (
          <ThreadStatusIcon thread={thread} />
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <p
            className={cn(
              "break-words text-sm font-medium leading-5",
              locked && "text-muted-foreground",
            )}
          >
            {thread.name || "Untitled conversation"}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {!locked && (
              <span className="text-xs text-muted-foreground">
                Last message at{" "}
                {format(thread.updatedAt, "MMM d, yyyy, h:mm a")}
              </span>
            )}
            {ownerName && (
              <>
                {!locked && (
                  <span className="text-xs text-muted-foreground/50">·</span>
                )}
                <span className="text-xs text-muted-foreground">
                  {ownerName}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 ml-2">
        {locked ? (
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
            Private
          </span>
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground" />
        )}
      </div>
    </>
  );

  if (locked) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-full flex items-center justify-between p-3 text-left cursor-default">
            {rowContent}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          You don&apos;t have access to this private conversation
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onClick(thread.id)}
      className={cn(
        "group w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
      )}
    >
      {rowContent}
    </button>
  );
};

interface ThreadStatusIconProps {
  thread: AgentSdkThread;
}

/** Renders a conversation icon, with a spinner only while the agent is running. */
function ThreadStatusIcon({ thread }: ThreadStatusIconProps) {
  if (thread.status === AGENT_SDK_THREAD_STATUS.ACTIVE) {
    return (
      <Loader2
        aria-label="Conversation running"
        className="h-4 w-4 text-muted-foreground shrink-0 animate-spin motion-reduce:animate-none"
      />
    );
  }
  return <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />;
}
